import { api } from './api';

// ==================== TYPES ====================

export type PageSectionType = 'BANNER' | 'MODULE' | 'COURSE' | 'EMPTY_SPACE';

export interface BannerSectionConfig {
  variant: 'course-hero' | 'custom';
  imageUrl?: string;
  title?: string;
  subtitle?: string;
  showProgress?: boolean;
  showStart?: boolean;
  showTrail?: boolean;
  showCategory?: boolean;
  cta?: { label: string; url: string; newTab?: boolean };
}

export interface ModuleSectionConfig {
  /** Ordered, hand-picked module ids (from any product). */
  moduleIds: string[];
  /** Section title; falls back to "Módulos". */
  title?: string;
  /** @deprecated legacy: a parent module ("group") id, before curated lists. */
  moduleGroupId?: string;
}

export interface CourseSectionConfig {
  title?: string;
  /** Ordered list of other course (product) ids to cross-promote. */
  courseIds: string[];
}

export interface EmptySpaceSectionConfig {
  height: 'sm' | 'md' | 'lg';
}

export type PageSectionConfig =
  | BannerSectionConfig
  | ModuleSectionConfig
  | CourseSectionConfig
  | EmptySpaceSectionConfig
  | Record<string, never>;

export interface PageSection {
  id: string;
  productId: string;
  type: PageSectionType;
  order: number;
  isVisible: boolean;
  config: PageSectionConfig;
  createdAt: string;
  updatedAt: string;
}

export interface CourseMenuItem {
  id: string;
  productId: string;
  title: string;
  icon?: string;
  url?: string;
  isExternal: boolean;
  openNewTab: boolean;
  order: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export type CreatePageSectionInput = {
  type: PageSectionType;
  order?: number;
  isVisible?: boolean;
  config?: PageSectionConfig;
};

export type UpdatePageSectionInput = Partial<{
  type: PageSectionType;
  order: number;
  isVisible: boolean;
  config: PageSectionConfig;
}>;

export type CreateCourseMenuItemInput = {
  title: string;
  icon?: string;
  url?: string;
  isExternal?: boolean;
  openNewTab?: boolean;
  order?: number;
  isActive?: boolean;
};

export type UpdateCourseMenuItemInput = Partial<CreateCourseMenuItemInput>;

function unwrap<T>(data: unknown): T {
  return ((data as { data?: T })?.data ?? data) as T;
}

// ==================== SERVICE ====================

class CourseAppearanceService {
  // ----- Page sections -----

  async getPageSections(productId: string): Promise<PageSection[]> {
    const { data } = await api.get(`/products/${productId}/page-sections`);
    return unwrap<PageSection[]>(data) || [];
  }

  async createPageSection(
    productId: string,
    input: CreatePageSectionInput,
  ): Promise<PageSection> {
    const { data } = await api.post(
      `/products/${productId}/page-sections`,
      input,
    );
    return unwrap<PageSection>(data);
  }

  async updatePageSection(
    productId: string,
    sectionId: string,
    input: UpdatePageSectionInput,
  ): Promise<PageSection> {
    const { data } = await api.put(
      `/products/${productId}/page-sections/${sectionId}`,
      input,
    );
    return unwrap<PageSection>(data);
  }

  async deletePageSection(productId: string, sectionId: string): Promise<void> {
    await api.delete(`/products/${productId}/page-sections/${sectionId}`);
  }

  async reorderPageSections(productId: string, ids: string[]): Promise<void> {
    await api.put(`/products/${productId}/page-sections/reorder`, { ids });
  }

  // ----- Per-course menu items (sidebar) -----

  async getMenuItems(productId: string): Promise<CourseMenuItem[]> {
    const { data } = await api.get(`/products/${productId}/menu-items`);
    return unwrap<CourseMenuItem[]>(data) || [];
  }

  async createMenuItem(
    productId: string,
    input: CreateCourseMenuItemInput,
  ): Promise<CourseMenuItem> {
    const { data } = await api.post(`/products/${productId}/menu-items`, input);
    return unwrap<CourseMenuItem>(data);
  }

  async updateMenuItem(
    productId: string,
    itemId: string,
    input: UpdateCourseMenuItemInput,
  ): Promise<CourseMenuItem> {
    const { data } = await api.put(
      `/products/${productId}/menu-items/${itemId}`,
      input,
    );
    return unwrap<CourseMenuItem>(data);
  }

  async deleteMenuItem(productId: string, itemId: string): Promise<void> {
    await api.delete(`/products/${productId}/menu-items/${itemId}`);
  }

  async reorderMenuItems(productId: string, ids: string[]): Promise<void> {
    await api.put(`/products/${productId}/menu-items/reorder`, { ids });
  }
}

export const courseAppearanceService = new CourseAppearanceService();
